import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Chip,
  Alert
} from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';

interface MarketAnalysis {
  risks?: string[];
  demandDrivers?: string[];
  summary?: string;
}

interface MarketAnalysisCardProps {
  analysis: any;  // Using any type due to unpredictable AI analysis structure
}

const MarketAnalysisCard: React.FC<MarketAnalysisCardProps> = ({ analysis }) => {
  // Extract market analysis data from potentially nested structure
  const extractMarketAnalysis = (): MarketAnalysis => {
    try {
      if (!analysis) return {};

      // Parse the data if it's a string, otherwise use it directly
      const analysisData = typeof analysis === 'string'
        ? JSON.parse(analysis)
        : analysis;

      // Try to access analysis data at different possible paths
      let marketAnalysis = null;

      if (analysisData.marketAnalysis) {
        marketAnalysis = analysisData.marketAnalysis;
      } else if (analysisData.analysis_data && analysisData.analysis_data.marketAnalysis) {
        marketAnalysis = analysisData.analysis_data.marketAnalysis;
      } else {
        // Look for any object with summary, risks, and demandDrivers properties
        for (const key in analysisData) {
          if (typeof analysisData[key] === 'object' &&
            analysisData[key] &&
            ('summary' in analysisData[key] || 'risks' in analysisData[key] || 'demandDrivers' in analysisData[key])) {
            marketAnalysis = analysisData[key];
            break;
          }
        }
      }

      // If still null, try to use the whole object as market analysis
      if (!marketAnalysis && typeof analysisData === 'object') {
        marketAnalysis = analysisData;
      }

      // Extract data from available fields
      const risks = Array.isArray((marketAnalysis as any)?.risks)
        ? (marketAnalysis as any).risks
        : [];

      const demandDrivers = Array.isArray((marketAnalysis as any)?.demandDrivers)
        ? (marketAnalysis as any).demandDrivers
        : [];

      const summary = (marketAnalysis as any)?.summary || '';

      return { risks, demandDrivers, summary };
    } catch (e) {
      console.error('Error parsing market analysis:', e);
      return {};
    }
  };

  const { risks = [], demandDrivers = [], summary = '' } = extractMarketAnalysis();
  const hasData = risks.length > 0 || demandDrivers.length > 0 || summary;

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#ffffff'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AnalyticsIcon sx={{ mr: 1, color: '#1a237e' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
          Market Analysis
        </Typography>
      </Box>

      {hasData ? (
        <Box sx={{ mb: 3 }}>
          {risks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#424242' }}>
                Key Risk Factors
              </Typography>
              {risks.map((risk: string, index: number) => (
                <Chip
                  key={index}
                  label={risk}
                  size="small"
                  sx={{
                    m: 0.5,
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    '& .MuiChip-label': {
                      fontSize: '0.75rem'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          {demandDrivers.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#424242' }}>
                Demand Drivers
              </Typography>
              {demandDrivers.map((driver: string, index: number) => (
                <Chip
                  key={index}
                  label={driver}
                  size="small"
                  sx={{
                    m: 0.5,
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    '& .MuiChip-label': {
                      fontSize: '0.75rem'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          {summary && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#424242' }}>
                Analysis Summary
              </Typography>
              <Typography variant="body2" sx={{ color: '#000000', whiteSpace: 'pre-line' }}>
                {summary}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant="body1" color="textSecondary">
          No market analysis data available
        </Typography>
      )}
    </Paper>
  );
};

export default MarketAnalysisCard;